import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import LeaderboardChart from './components/LeaderboardChart';

const App = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const objective = 100;

  const dayName = moment().format('dddd');

  // Memoize calculateCumulativeValues to prevent recreation
  const calculateCumulativeValues = useCallback((member, category, tt = false) => {
    let dpTotal = 0;
    let dvTotal = 0;

    if (tt) {
      dpTotal =
        member.dp_monday +
        member.dp_tuesday +
        member.dp_wednesday +
        member.dp_thursday +
        member.dp_friday;
      dvTotal =
        member.dv_monday +
        member.dv_tuesday +
        member.dv_wednesday +
        member.dv_thursday +
        member.dv_friday;
      const taux = dpTotal === 0 ? 0 : ((dvTotal / dpTotal) * 100).toFixed(2);
      return { dpTotal, dvTotal, taux };
    }

    switch (dayName) {
      case 'Monday':
        dpTotal = member.dp_monday;
        dvTotal = member.dv_monday;
        break;
      case 'Tuesday':
        dpTotal = member.dp_monday + member.dp_tuesday;
        dvTotal = member.dv_monday + member.dv_tuesday;
        break;
      case 'Wednesday':
        dpTotal = member.dp_monday + member.dp_tuesday + member.dp_wednesday;
        dvTotal = member.dv_monday + member.dv_tuesday + member.dv_wednesday;
        break;
      case 'Thursday':
        dpTotal =
          member.dp_monday + member.dp_tuesday + member.dp_wednesday + member.dp_thursday;
        dvTotal =
          member.dv_monday + member.dv_tuesday + member.dv_wednesday + member.dv_thursday;
        break;
      case 'Friday':
        dpTotal =
          member.dp_monday +
          member.dp_tuesday +
          member.dp_wednesday +
          member.dp_thursday +
          member.dp_friday;
        dvTotal =
          member.dv_monday +
          member.dv_tuesday +
          member.dv_wednesday +
          member.dv_thursday +
          member.dv_friday;
        break;
      default:
        dpTotal = 0;
        dvTotal = 0;
        break;
    }

    const taux = dpTotal === 0 ? 0 : ((dvTotal / dpTotal) * 100).toFixed(2);
    return { dpTotal, dvTotal, taux };
  }, [dayName]); // Include dayName as a dependency since it’s used in the function

  useEffect(() => {
    let isMounted = true; // Track if component is mounted

    const fetchData = async () => {
      if (!isMounted) return; // Prevent state updates if unmounted
      try {
        setLoading(true); // Set loading state for each fetch
        const proxyBase = "https://corss-smoky.vercel.app/api/proxy?url=";
        const response = await fetch(`${proxyBase}${encodeURIComponent('https://test.eurodistech.com/storage/json/commercial-detail.json')}`);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Response is not JSON');
        }

        const result = await response.json();
        const allData = [];
        const teamTotals = {
          commercial: { dpTotal: 0, dvTotal: 0 },
          sourcing: { dpTotal: 0, dvTotal: 0 },
          relance: { dpTotal: 0, dvTotal: 0 },
        };

        // Process each category
        const categories = ['commercial', 'sourcing', 'relance'];
        for (const category of categories) {
          const categoryData = result[category];
          if (!categoryData || !Array.isArray(categoryData)) {
            console.warn(`No data found for ${category}`);
            continue;
          }

          const formattedData = categoryData.map((user) => {
            const { dpTotal, dvTotal, taux } = calculateCumulativeValues(user, category);
            teamTotals[category].dpTotal += dpTotal;
            teamTotals[category].dvTotal += dvTotal;
            return {
              id: user.id_user,
              name: user.full_name || `${user.prenom} ${user.nom}`,
              value: parseFloat(taux),
              avatar: `https://www.eurodistech.com/erp/public/assets/images/avatars/${user.avatar}`,
              taux_conversion: user.taux_conversion || 0,
              nc_week: user.nc_week || 0,
              category,
            };
          });

          allData.push(...formattedData);
        }

        // Add managers for each team
        const managers = [
          {
            id: 'commercial_manager',
            name: 'Meriem Rahib',
            category: 'Responsable commercial',
            avatar_url:
              'https://www.eurodistech.com/erp/public/assets/images/avatars/rahib-meryem-25-thumb.jpg',
            totals: teamTotals.commercial,
          },
          {
            id: 'sourcing_manager',
            name: 'Hafid Elhouaf',
            category: 'Responsable sourcing',
            avatar_url:
              'https://www.eurodistech.com/erp/public/assets/images/avatars/elhaouaf-hafid-29-thumb.jpg',
            totals: teamTotals.sourcing,
          },
          {
            id: 'relance_manager',
            name: 'Omar Benjelouaja',
            avatar_url:
              'https://www.shutterstock.com/image-vector/default-avatar-profile-icon-vector-600nw-1745180411.jpg',
            category: 'Responsable relance',
            totals: teamTotals.relance,
          },
        ];

        managers.forEach((manager) => {
          const { dpTotal, dvTotal } = manager.totals;
          const taux = dpTotal === 0 ? 0 : ((dvTotal / dpTotal) * 100).toFixed(2);
          allData.push({
            id: manager.id,
            name: manager.name,
            value: parseFloat(taux),
            avatar: manager.avatar_url,
            taux_conversion: 0,
            nc_week: 0,
            category: manager.category,
          });
        });

        if (isMounted) {
          setData(allData.sort((a, b) => b.value - a.value));
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        if (isMounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    };

    // Initial fetch
    fetchData();

    // Set up interval for periodic fetching
    const refreshInterval = setInterval(fetchData, 300000);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      clearInterval(refreshInterval);
    };
  }, [calculateCumulativeValues]); // Dependency is now memoized

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading team data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <h3>Error Loading Data</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <LeaderboardChart data={data} objective={objective} showConversion={true} />
    </div>
  );
};

export default App;